import type { GridProps } from '@chakra-ui/react';
import { Box, Grid, Flex, Text, Link, Skeleton } from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import React from 'react';

import type { CustomLinksGroup } from 'types/footerLinks';

import config from 'configs/app';
import type { ResourceError } from 'lib/api/resources';
import useApiQuery from 'lib/api/useApiQuery';
import useFetch from 'lib/hooks/useFetch';
import NetworkAddToWallet from 'ui/shared/NetworkAddToWallet';

import CarbonZeroLogo from './CarbonZeroLogo';
import FooterLinkItem from './FooterLinkItem';
import IntTxsIndexingStatus from './IntTxsIndexingStatus';
// import getApiVersionUrl from './utils/getApiVersionUrl';

const MAX_LINKS_COLUMNS = 4;

/* const FRONT_VERSION_URL = `https://github.com/blockscout/frontend/tree/${ config.UI.footer.frontendVersion }`;
const FRONT_COMMIT_URL = `https://github.com/blockscout/frontend/commit/${ config.UI.footer.frontendCommit }`;
 */
const Footer = () => {

  const { } = useApiQuery('config_backend_version', {
    queryOptions: {
      staleTime: Infinity,
    },
  });
  // const apiVersionUrl = getApiVersionUrl(backendVersionData?.backend_version);
  const BLOCKSCOUT_LINKS: Array<React.ComponentProps<typeof FooterLinkItem>> = [
    {
      icon: 'link',
      iconSize: '24px',
      text: 'Portal',
      url: 'https://rizenet.io/',
    },
    {
      icon: 'social/telegram_filled',
      iconSize: '24px',
      text: 'Telegram',
      url: 'https://t.me/Rizenet_OfficialCommunity/',
    },
    {
      icon: 'social/discord_filled',
      iconSize: '24px',
      text: 'Discord',
      url: 'https://discord.gg/rizenet',
    },
  ];

  /*   const frontendLink = (() => {
      if (config.UI.footer.frontendVersion) {
        return <Link href={ FRONT_VERSION_URL } target="_blank">{ config.UI.footer.frontendVersion }</Link>;
      }

      if (config.UI.footer.frontendCommit) {
        return <Link href={ FRONT_COMMIT_URL } target="_blank">{ config.UI.footer.frontendCommit }</Link>;
      }

      return null;
    })(); */

  const fetch = useFetch();

  const { isPlaceholderData, data: linksData } = useQuery<unknown, ResourceError<unknown>, Array<CustomLinksGroup>>({
    queryKey: [ 'footer-links' ],
    queryFn: async() => fetch(config.UI.footer.links || '', undefined, { resource: 'footer-links' }),
    enabled: Boolean(config.UI.footer.links),
    staleTime: Infinity,
    placeholderData: [],
  });

  const colNum = isPlaceholderData ? 1 : Math.min(linksData?.length || Infinity, MAX_LINKS_COLUMNS) + 1;

  const renderNetworkInfo = React.useCallback((gridArea?: GridProps['gridArea']) => {
    return (
      <Flex
        gridArea={ gridArea }
        flexWrap="wrap"
        columnGap={ 8 }
        rowGap={ 6 }
        mb={{ base: 5, lg: 10 }}
        _empty={{ display: 'none' }}
      >
        { !config.UI.indexingAlert.intTxs.isHidden && <IntTxsIndexingStatus/> }
        <NetworkAddToWallet/>
      </Flex>
    );
  }, []);

  const renderProjectInfo = React.useCallback((gridArea?: GridProps['gridArea']) => {
    return (
      <Box gridArea={ gridArea }>
        <CarbonZeroLogo/>
        <Text mt={ 5 } fontSize="md" fontWeight={ 600 }>
          Rizenet Testnet Explorer
        </Text>
        <Text mt={ 1 } fontSize="sm" color="text_secondary" lineHeight="20px">
          Explore transactions, blocks, addresses and token activity on the Rizenet Testnet.
        </Text>
        <Box
          as="aside"
          aria-label="Testnet notice"
          mt={ 4 }
          px={ 3 }
          py={ 2 }
          borderLeftWidth="2px"
          borderColor="orange.400"
          bgColor="blackAlpha.50"
          _dark={{ bgColor: 'whiteAlpha.50' }}
          borderRadius="base"
        >
          <Text fontSize="xs" fontWeight={ 600 } textTransform="uppercase" letterSpacing="0.04em">
            Testnet notice
          </Text>
          <Text mt={ 1 } fontSize="xs" color="text_secondary" lineHeight="18px">
            { 'This explorer displays testnet activity for development and testing purposes only. ' +
              'Token balances, transactions and contracts shown here do not represent mainnet activity or market usage.' }
          </Text>
        </Box>
        <Text mt={ 4 } fontSize="sm" color="text_secondary" fontWeight={ 200 }>
          Operated by CarbonZero Foundation Company (“CarbonZero”)
        </Text>
        <Text mt={ 4 } fontSize="xs" color="text_secondary">
          Powered By
          <Link fontSize="xs" href="https://github.com/blockscout/blockscout"> Blockscout</Link>
        </Text>

        { /*         <VStack spacing={ 1 } mt={ 6 } alignItems="start">
          { apiVersionUrl && (
            <Text fontSize="xs">
              Backend: <Link href={ apiVersionUrl } target="_blank">{ backendVersionData?.backend_version }</Link>
            </Text>
          ) }
          { frontendLink && (
            <Text fontSize="xs">
              Frontend: { frontendLink }
            </Text>
          ) }
        </VStack> */ }
      </Box>
    );
  }, [ /* apiVersionUrl, backendVersionData?.backend_version, frontendLink  */]);

  const containerProps: GridProps = {
    as: 'footer',
    px: { base: 4, lg: 12 },
    py: { base: 4, lg: 9 },
    borderTop: '1px solid',
    borderColor: 'divider',
    gridTemplateColumns: { base: '1fr', lg: 'minmax(auto, 470px) 1fr' },
    columnGap: { lg: '32px', xl: '100px' },
  };

  if (config.UI.footer.links) {
    return (
      <Grid { ...containerProps }>
        <div>
          { renderNetworkInfo() }
          { renderProjectInfo() }
        </div>

        <Grid
          gap={{ base: 6, lg: colNum === MAX_LINKS_COLUMNS + 1 ? 2 : 8, xl: 12 }}
          gridTemplateColumns={{
            base: 'repeat(auto-fill, 160px)',
            lg: `repeat(${ colNum }, 135px)`,
            xl: `repeat(${ colNum }, 160px)`,
          }}
          justifyContent={{ lg: 'flex-end' }}
          mt={{ base: 8, lg: 0 }}
        >
          {
            ([
              { title: 'Blockscout', links: BLOCKSCOUT_LINKS },
              ...(linksData || []),
            ])
              .slice(0, colNum)
              .map(linkGroup => (
                <Box key={ linkGroup.title }>
                  <Skeleton fontWeight={ 500 } mb={ 3 } display="inline-block" isLoaded={ !isPlaceholderData }>{ linkGroup.title }</Skeleton>
                  { /*<VStack spacing={ 1 } alignItems="start">
                    { linkGroup.links.map(link => <FooterLinkItem { ...link } key={ link.text } isLoading={ isPlaceholderData }/>) }
                  </VStack> */ }
                </Box>
              ))
          }
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid
      { ...containerProps }
      gridTemplateAreas={{
        lg: `
          "network links-top"
          "info links-bottom"
        `,
      }}
    >

      { renderNetworkInfo({ lg: 'network' }) }
      { renderProjectInfo({ lg: 'info' }) }

      <Grid
        gridArea={{ lg: 'links-bottom' }}
        columnGap={ 8 }
        rowGap={ 1 }
        gridTemplateColumns={{
          base: 'repeat(auto-fill, 160px)',
          lg: 'repeat(3, auto)',
        }}
        gridAutoFlow="row"
        alignContent="start"
        justifyContent={{ lg: 'flex-end' }}
        mt={{ base: 8, lg: 0 }}
      >
        { BLOCKSCOUT_LINKS.map(link => <FooterLinkItem { ...link } key={ link.text }/>) }
      </Grid>
    </Grid>
  );
};

export default React.memo(Footer);
